import { getParent, resolveIdentifier, types, flow } from 'mobx-state-tree';
import {
  CatalogItem,
  ListParams,
  CatalogItemsPagination,
  CatalogItemsParams,
  Category,
  CategoryNode,
} from '../../models/Catalog';
import ENUMS from '../../constants/enums';

const URL = 'catalog';

const CatalogStore = types
  .model('CatalogStore', {
    hotItems: types.optional(types.array(CatalogItem), []),
    hotItemsOpened: true,
    visibleIndexStart: 0,
    // items that are hot on the current page are references to an item from hotItems
    items: types.optional(types.array(types.union(CatalogItem, types.reference(CatalogItem))), []),
    otherPageItem: types.maybe(CatalogItem),
    listParams: types.optional(ListParams, {}),
    categories: types.optional(types.array(Category), []),
    categoriesTree: types.optional(types.array(types.late(CategoryNode)), []),
    locations: types.optional(types.array(types.frozen), []),
    catalogItemsParams: types.optional(CatalogItemsParams, {}),
    catalogItemsPagination: types.optional(CatalogItemsPagination, {}),
  })
  .views(self => ({
    get transport5007() {
      return getParent(self).transport5007;
    },
    get accountCode() {
      return getParent(self).sessionStore.accountCode;
    },
    get isNonApi() {
      return getParent(self).sessionStore.isNonApi;
    },
    get defaultQuantityToList() {
      return getParent(self).settingsStore.listing.listingOptions.defaultQuantityToList || 1;
    },
    get termsAccepted() {
      return getParent(self).settingsStore.userProfile.termsAccepted;
    },
    get updateTermsAccepted() {
      return getParent(self).settingsStore.userProfile.updateTermsAccepted;
    },
    get getDiscoveryStrategy() {
      return getParent(self).discoveryStore.discoverStrategy;
    },
    get rangedProfitSelected() {
      return getParent(self).discoveryStore.discoverStrategy === 'ranged';
    },
    get strategies() {
      return getParent(self).discoveryStore.strategies;
    },
    get policyCode() {
      return getParent(self).discoveryStore.getDefaultPolicyCode()?.code ?? '';
    },
    get currentHotItemsPage() {
      return self.hotItemsOpened
        ? self.hotItems.slice(self.visibleIndexStart, self.visibleIndexStart + 6)
        : [];
    },
    get sellerAccountCode() {
      return getParent(self).discoveryStore.sellerAccountCode;
    },
    get unlimitedItems() {
      return self.items.filter(item => !item.limited);
    },
    get unlimitedHotItemsCurrentPage() {
      return self.currentHotItemsPage.filter(item => !item.limited);
    },
    get selectedItemsIds() {
      return [
        ...self.items.reduce((acc, curr) => (curr.selected ? [...acc, curr] : acc), []),
        ...self.hotItems.reduce((acc, curr) => (curr.selected ? [...acc, curr] : acc), []),
      ];
    },
    get selectedItemsCount() {
      return (
        self.items.filter(item => item.selected).length +
        self.hotItems.filter(item => item.selected).length
      );
    },
    get allSelected() {
      return (
        self.unlimitedItems.reduce((acc, item) => {
          return item.selected && acc;
        }, true) &&
        self.unlimitedHotItemsCurrentPage.reduce((acc, item) => {
          return item.selected && acc;
        }, true)
      );
    },
    get hotItemIdsMap() {
      return self.hotItems.reduce((acc, { id }) => ({ ...acc, [id]: true }), {});
    },
  }))
  .actions(self => ({
    itemReference(identifier) {
      if (typeof identifier !== 'number' && typeof identifier !== 'string') {
        return {};
      }
      return self.hotItemIdsMap[identifier]
        ? resolveIdentifier(CatalogItem, self.hotItems, identifier)
        : resolveIdentifier(CatalogItem, self.items, identifier);
    },
    setVisibleIndexStart(index) {
      if (index + 6 > self.hotItems.length) {
        self.visibleIndexStart = self.hotItems.length - 6;
      } else {
        self.visibleIndexStart = index;
      }
    },
    setSelectedItem(val, identifier) {
      self.itemReference(identifier).setSelected(val);
    },
    selectAll(val) {
      self.unlimitedItems.forEach(item => {
        item.setSelected(val);
      });
      self.unlimitedHotItemsCurrentPage.forEach(item => {
        item.setSelected(val);
      });
    },
    loadPage: flow(function* loadPage(pageNumber) {
      self.catalogItemsPagination.setPage(pageNumber);
      yield self.fetchItems();
    }),
    initStore: flow(function*() {
      yield self.fetchHotItems();
      yield self.fetchItems();
      yield self.fetchCategories();
    }),
    fetchCategories: flow(function*() {
      try {
        const { data } = yield self.transport5007.get(`${URL}/categories/active`);
        self.categories = data;
        self.categoriesTree = data
          .filter(({ parentCategoryId }) => parentCategoryId === null)
          .map(({ id, name }) => ({ label: name, value: id }));
      } catch (e) {
        console.error(e);
      }
    }),
    fetchItems: flow(function* fetchItems() {
      try {
        const { pageNumber, pageSize } = self.catalogItemsPagination;
        const params = {
          pageNumber,
          pageSize,
          ...self.catalogItemsParams,
        };
        const {
          data: { totalResults, data },
        } = yield self.transport5007.post(`${URL}/items/filtered`, params);
        self.catalogItemsPagination.setTotalResults(totalResults);
        const items = [];
        data.forEach(item => {
          // if(item.isPromotion && !self.hotItemIdsMap[item.id]){
          //   self.hotItems.push(item)
          // }
          if (item.isPromotion) {
            if (self.hotItemIdsMap[item.id]) {
              items.push(item.id);
            } else {
              items.push(item);
            }
          } else {
            items.push(item);
          }
        });
        self.items = items;
      } catch (error) {
        console.error('Getting Catalog Items failed: ', error);
      }
    }),
    fetchItem: flow(function* fetchItem(id) {
      try {
        const { data } = yield self.transport5007.fetch(`${URL}/items?id=${id}`);
        self.otherPageItem = { ...data, id: `____${data.id}` }; // to prevent resolveItem errors when on the same page
      } catch (error) {
        console.error('Getting Catalog Item failed: ', error);
      }
    }),
    fetchHotItems: flow(function* fetchHotItems() {
      try {
        const { data } = yield self.transport5007.get(`${URL}/items/promoted`);
        self.hotItems = data;
      } catch (error) {
        console.error('Getting Catalog Promoted Items failed: ', error);
      }
    }),
    listItems: flow(function* listItems(catId) {
      try {
        if (!self.isNonApi) {
          if (!self.policyCode) {
            return {
              errorMessage: 'No Business Policy Data',
            };
          }
        }
        const reqData = {
          accountCode: self.accountCode,
          isWithVariations: false,
          sellerAccountCode: self.sellerAccountCode,
          discoveryBody: catId
            ? [{ id: catId, title: null }]
            : self.selectedItemsIds.map(({ id }) => ({ id, title: null })),
          sourceId: ENUMS.NEW_DISCOVERY_SOURCE_NAMES.CATALOG,
          destinationId: ENUMS.DESTINATION_MARKETPLACE.EBAY_US,
          discoveryMethod: 2,
          defaultQuantity: self.defaultQuantityToList,
          policyCode: self.isNonApi ? '' : self.policyCode,
          discoveryStrategy: self.rangedProfitSelected
            ? self.strategies.toJSON()[ENUMS.STRATEGY.FIXED_PROFIT].properties
            : self.strategies.toJSON()[self.getDiscoveryStrategy].properties,
          skipVeroValidation: self.listParams.skipVeroValidation,
          usePredefined: self.rangedProfitSelected,
        };

        const { data } = yield self.transport5007.post(
          'discovery-new/v1/pre-discovery',
          reqData,
          null,
          {},
        );
        if (!catId) {
          self.selectAll(false);
        }
        return data;
      } catch (error) {
        console.error('Getting Catalog Item failed: ', error);
        const { data } = error?.response ?? false;
        return (
          data || {
            errorMessage: 'There was an error during the listing process',
          }
        );
      }
    }),
    removeOtherPageItem() {
      self.otherPageItem = null;
    },
    setHotItemsOpened(val) {
      self.hotItemsOpened = val;
    },
    // initStore: flow(function* initStore() {
    //   try {
    //   } catch (error) {
    //     console.error(error);
    //   }
    // }),
  }));

export default CatalogStore;
